import { BlockManifest } from '@template-ui/blocks';
import {
  noticeCommandCodes,
  sdmfCommandCodes,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import { votingOperations } from '@app/shared/workflow-voting/voting.models';
import { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';
import {
  addWorkflowCapability,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';

/** Commands selected atomically with an interactive block, including their dependencies. */
export function blockOwnedCommandCodes(
  manifest: BlockManifest,
): readonly string[] {
  if (
    manifest.provider === 'hearing' &&
    ['hearing.panel', 'hearing.decision'].includes(manifest.code)
  )
    return [
      manifest.code === 'hearing.panel'
        ? 'hearing.save-panel'
        : 'hearing.finalize-decision',
    ];
  if (manifest.provider === 'scheduling')
    return [
      'scheduling.request',
      'scheduling.book',
      'scheduling.reschedule',
      'scheduling.remove',
      'scheduling.withdraw',
    ];
  if (manifest.provider === 'voting')
    return (votingOperations[manifest.code] ?? []).map(
      (operation) => `voting.${operation}`,
    );
  if (manifest.code === 'notices.delivery') return [...noticeCommandCodes];
  if (manifest.code === 'sdmf.analysis') return [...sdmfCommandCodes];
  return [];
}

/** Layout versions can retain the actions from their original behavior contract. */
export function blockOwnedCommandVersion(manifest: BlockManifest): number {
  // Presentation versions retain the installed domain command contracts.
  return ['scheduling', 'hearing', 'notices'].includes(manifest.provider) ||
    (manifest.provider === 'voting' &&
      manifest.code === 'voting.ballots' &&
      manifest.version === 2)
    ? 1
    : manifest.version;
}

/** Validate the exact installed closure on a disposable document; never repin saved contracts. */
export function blockCapabilityReason(
  manifest: BlockManifest,
  document: WorkflowDefinitionDocument,
  catalog: readonly WorkflowCapabilityCatalogEntry[] | null,
): string {
  const required = [
    ...manifest.reads.map((read) => ({
      provider: manifest.provider,
      kind: 'view',
      code: read.code,
      version: read.version,
    })),
    ...blockOwnedCommandCodes(manifest).map((code) => ({
      provider: manifest.provider,
      kind: 'command',
      code,
      version: blockOwnedCommandVersion(manifest),
    })),
  ];
  if (!required.length) return '';
  if (!catalog) return 'Checking available content…';
  let candidate = document;
  try {
    for (const key of required) {
      const entries = catalog.filter(
        (entry) =>
          entry.key.provider === key.provider &&
          entry.key.kind === key.kind &&
          entry.key.code === key.code &&
          entry.key.version === key.version,
      );
      if (entries.length !== 1)
        return `${manifest.name} requires an installed ${key.kind === 'view' ? 'data source' : 'action'} that is unavailable.`;
      const entry = entries[0];
      if (entry.lifecycle !== 'available')
        return (
          entry.unavailableReason ||
          `${manifest.name} is unavailable for new use.`
        );
      candidate = addWorkflowCapability(candidate, entry, catalog);
    }
    return '';
  } catch (error) {
    return error instanceof Error
      ? error.message
      : `${manifest.name} has an unavailable dependency.`;
  }
}

/** Prefer a usable version within each family; incompatible newer contracts never hide usable older ones. */
export function preferredBlockCatalog(
  manifests: readonly BlockManifest[],
  reason: (manifest: BlockManifest) => string,
): { available: BlockManifest[]; unavailable: BlockManifest[] } {
  const preferred = new Map<string, BlockManifest>();
  for (const manifest of manifests) {
    if (manifest.deprecated || reason(manifest)) continue;
    const family = `${manifest.provider}/${manifest.code}`;
    const current = preferred.get(family);
    if (!current || manifest.version > current.version)
      preferred.set(family, manifest);
  }
  const selected = new Set(preferred.values());
  return {
    available: manifests.filter((manifest) => selected.has(manifest)),
    unavailable: manifests.filter((manifest) => !selected.has(manifest)),
  };
}
